import { ChangeEvent, useCallback, useEffect, useState } from "react";
import { unwrapResult } from "@reduxjs/toolkit";
import { useDispatch } from "react-redux";
import { useSearchParams } from "react-router-dom";

import {
  CustomButton,
  CustomCheckBox,
  CustomTable,
  SearchSelect,
} from "../../../shared/uiComponents";
import { SelectDataProperties } from "../../../shared/uiComponents/Dropdown/searchSelect";
import { errorNormalizer } from "../../../shared/Helpers/functions";
import { Headers, TableBody } from "./tableInfo";
import { downloadFile, ReportFilters } from "../helpers";

import { DispatchProperties, useSelector } from "../../../redux/store";
import { GetBillingProperties } from "../../../redux/API/ClientAPIHelpers/billingProperties";
import {
  downloadBackdatedSessions,
  getBackdatedSessions,
} from "../../../redux/State/clientSlice/billingSlice";
import { getAdmins } from "../../../redux/State/userSlice";

const BackdatedSessions = () => {
  const dispatch = useDispatch<DispatchProperties>();
  const [params] = useSearchParams();

  const [onlySubmitted, setOnlySubmitted] = useState<boolean>(true);
  const [credentialed, setCredentialed] = useState<boolean>(true);
  const [pageSize, setPageSize] = useState<number>(8);
  const [searchString, setSearchString] = useState<string>("");
  const [createdById, setCreatedById] = useState<string>("");
  const [usersData, setUsersData] = useState<Array<SelectDataProperties>>([]);

  const sessions = useSelector((state) => state.billing.backdatedSessions);
  const loading = useSelector((state) => state.billing.loading);
  const {
    clientIds,
    endDate,
    startDate,
    userId,
    onlyNew,
    timeError,
  } = useSelector((state) => state.billing.billingFilters);
  const loadingUsers = useSelector((state) => state.user.loading);

  const fetchData = useCallback(() => {
    const page = params.get("page") || "1";
    const pageSize = params.get("pageSize") || "8";
    const direction = params.get("direction") || "";
    const orderBy = params.get("orderBy") || "";
    const searchString = params.get("searchString") || "";

    if (timeError) return;

    if (!startDate || !endDate) return;
    const data: GetBillingProperties = {
      page,
      pageSize,
      startDate,
      endDate,
      onlyNew,
      direction,
      orderBy,
      searchString,
      userId,
      clientIds,
      createdById,
      onlySubmitted,
      credentialed,
    };

    dispatch(getBackdatedSessions(data))
      .then(unwrapResult)
      .catch(errorNormalizer);
  }, [dispatch, params, clientIds,
    endDate,
    startDate,
    userId,
    onlyNew,
    timeError,
    createdById,
    onlySubmitted,
    credentialed,
  ]);

  const downloadHandler = useCallback(() => {
    const page = params.get("page") || "1";
    const direction = params.get("direction") || "";
    const orderBy = params.get("orderBy") || "";
    const searchString = params.get("searchString") || "";
    const pageSize = !!sessions.totalNumberOfItems
      ? sessions.totalNumberOfItems.toString()
      : "8";

    if (!startDate || !endDate) return;

    const data: GetBillingProperties = {
      page,
      pageSize,
      startDate,
      endDate,
      onlyNew,
      direction,
      orderBy,
      searchString,
      userId,
      clientIds,
      createdById,
      credentialed,
    };
    dispatch(downloadBackdatedSessions(data))
      .then(unwrapResult)
      .then((response) => {
        downloadFile(response);
        fetchData();
      })
      .catch(errorNormalizer);
  }, [dispatch, fetchData, params, sessions, clientIds,
    endDate,
    startDate,
    userId,
    onlyNew,
    createdById,
    credentialed,
  ]);

  const showOnlySubmittedHandler = (event: ChangeEvent<HTMLInputElement>) => {
    const { checked } = event.target;
    setOnlySubmitted(checked);
  };

  const toggleCreadentialedHandler = (event: ChangeEvent<HTMLInputElement>) => {
    const { checked } = event.target;
    setCredentialed(checked);
  };

  useEffect(() => {
    const timeout = setTimeout(fetchData, 1000);
    return () => clearTimeout(timeout);
  }, [fetchData]);

  useEffect(() => {
    const page = params.get("page") || "1";
    const timeout = setTimeout(() => {
      dispatch(getAdmins({ page, pageSize: `${pageSize}`, searchString }))
        .then(unwrapResult)
        .then((response) => {
          if (!response.query) return;
          const data: Array<SelectDataProperties> = response.query.map((user) => ({
            id: user.id,
            label: user.fullName,
          }));
          setUsersData(data);
        });
    }, 1000);
    return () => clearTimeout(timeout);
  }, [params, dispatch, pageSize, searchString]);

  const userHandler = (value: string) => {
    setCreatedById(value);
  };

  return (
    <>
      <>
        <div style={{ display: "flex", maxWidth: "720px", gap: "16px" }}>
          <div style={{ width: "250px", marginBottom: "16px" }}>
            <SearchSelect
              label="Find Admin:"
              searchString={searchString}
              setSearchString={setSearchString}
              data={usersData}
              setValue={userHandler}
              loadMore={{
                activate: true,
                setSize: setPageSize,
              }}
              loading={loadingUsers}
            />
          </div>
          <CustomCheckBox
            item={{
              label: "Show only submitted",
              id: "0",
              checked: onlySubmitted,
              disabled: loading,
            }}
            onChange={showOnlySubmittedHandler}
          />
          <CustomCheckBox
            item={{
              label: "Credentialed",
              id: "1",
              checked: credentialed,
              disabled: loading,
            }}
            onChange={toggleCreadentialedHandler}
          />
        </div>
        <ReportFilters
          showUsers={true}
          isMultipleSelect={true}
        />
        <div style={{ display: "flex", maxWidth: "720px", gap: "16px" }}>
          <CustomButton
            onClick={downloadHandler}
            title={"Download"}
            loading={loading}
            disabled={!onlySubmitted || timeError}
          />
        </div>
      </>
      <CustomTable
        headers={Headers}
        TableBody={TableBody}
        data={sessions}
        loading={loading}
        hasMobile={false}
      />
    </>
  );
};

export default BackdatedSessions;
